import React from 'react';

import { cn } from '@datanomik/core-ui';

import { useSystemNotificationsContext } from './context';
import { SnackbarToast } from './SnackbarToast';

const STACK = [
  'z-[1200] fixed top-[70px] right-5 w-full max-w-[340px]',
  '[.dn-has-banner_&]:top-[125px]!',

  '[&.dn-snackbar-stacked]:h-[86px]',
  '[&.dn-snackbar-stacked]:top-16',
  '[&.dn-snackbar-stacked]:[transition:all_0.25s_ease-in-out_0s]',
  '[&.dn-snackbar-stacked]:hover:h-[40vh]',
  '[&.dn-snackbar-stacked]:hover:[&_>_div]:[&:nth-of-type(2)]:[transform:none]',
  '[&.dn-snackbar-stacked]:hover:[&_>_div]:[&:nth-of-type(3)]:[transform:none]',
  '[&.dn-snackbar-stacked]:[&_>_div]:[transition:all_0.25s_ease-in-out_0s]',
  '[&.dn-snackbar-stacked]:[&_>_div]:[overflow:initial]!',
  '[&.dn-snackbar-stacked]:[&_>_div]:[&:nth-of-type(1)]:relative',
  '[&.dn-snackbar-stacked]:[&_>_div]:[&:nth-of-type(1)]:z-[3]',
  '[&.dn-snackbar-stacked]:[&_>_div]:[&:nth-of-type(2)]:relative',
  '[&.dn-snackbar-stacked]:[&_>_div]:[&:nth-of-type(2)]:z-[2]',
  '[&.dn-snackbar-stacked]:[&_>_div]:[&:nth-of-type(2)]:[transform:scale(0.94)_translateY(-92%)]',
  '[&.dn-snackbar-stacked]:[&_>_div]:[&:nth-of-type(3)]:relative',
  '[&.dn-snackbar-stacked]:[&_>_div]:[&:nth-of-type(3)]:z-[1]',
  '[&.dn-snackbar-stacked]:[&_>_div]:[&:nth-of-type(3)]:[transform:scale(0.88)_translateY(-200%)]',
  '[&.dn-snackbar-stacked]:[&_>_div]:[&:nth-of-type(n+4)]:opacity-[0]',
  '[&.dn-snackbar-stacked]:[&_>_div]:[&:nth-of-type(n+4)]:pointer-events-none',
  '[&.dn-snackbar-stacked]:[&_>_div]:[&:nth-of-type(n+4)]:[transform:scale(0.5)_translateY(70%)]',
].join(' ');

const ALERT = [
  '[&_[data-slot=alert]]:mt-2 [&_[data-slot=alert]]:p-[10px_12px] [&_[data-slot=alert]]:rounded-xl',
  '[&_[data-slot=alert]]:bg-[#FFFFFF] [&_[data-slot=alert]]:border [&_[data-slot=alert]]:border-solid [&_[data-slot=alert]]:border-border',
  '[&_[data-slot=alert]]:[font-family:Inter] [&_[data-slot=alert]]:text-[12px] [&_[data-slot=alert]]:font-normal',
  '[&_[data-slot=alert]]:leading-[16px] [&_[data-slot=alert]]:text-left [&_[data-slot=alert]]:text-[#525866]',
  '[&_[data-slot=alert]]:shadow-[0px_8px_24px_rgba(11,_18,_32,_0.10)] [&_[data-slot=alert]]:items-start',
  '[&_[data-slot=alert]]:dark:bg-[#1A1D27] [&_[data-slot=alert]]:dark:border [&_[data-slot=alert]]:dark:border-solid',
  '[&_[data-slot=alert]]:dark:border-[#31353F] [&_[data-slot=alert]]:dark:text-[#CDD0D5]',

  '[&_[data-slot=alert]]:[&_a]:text-[#2845D2] [&_[data-slot=alert]]:[&_a]:no-underline',
  '[&_[data-slot=alert]]:[&_a]:ml-1 [&_[data-slot=alert]]:[&_a]:font-medium',
  '[&_[data-slot=alert]]:[&_a]:[&.dn-ramove-margin]:m-0',
  '[&_[data-slot=alert]]:[&_a]:hover:underline',
  '[&_[data-slot=alert]]:[&_a]:dark:text-[#5B7BF5]',

  '[&_[data-slot=alert]]:[&_[data-slot=alert-content]]:p-0 [&_[data-slot=alert]]:[&_[data-slot=alert-content]]:w-full',
  '[&_[data-slot=alert]]:[&_[data-slot=alert-content]]:[&_[data-slot=text]]:text-[12px]',
  '[&_[data-slot=alert]]:[&_[data-slot=alert-content]]:[&_[data-slot=text]]:font-normal',
  '[&_[data-slot=alert]]:[&_[data-slot=alert-content]]:[&_[data-slot=text]]:leading-[16px]',
  '[&_[data-slot=alert]]:[&_[data-slot=alert-content]]:[&_[data-slot=text]]:text-left',
  '[&_[data-slot=alert]]:[&_[data-slot=alert-content]]:[&_[data-slot=text]]:text-[inherit]',

  '[&_[data-slot=alert]]:[&_[data-slot=alert-icon]]:p-0 [&_[data-slot=alert]]:[&_[data-slot=alert-icon]]:mr-[10px]',
  '[&_[data-slot=alert]]:[&_[data-slot=alert-icon]]:text-[18px] [&_[data-slot=alert]]:[&_[data-slot=alert-icon]]:text-[#F59E0B]',

  '[&_[data-slot=alert]]:[&_[data-slot=alert-action]]:p-0 [&_[data-slot=alert]]:[&_[data-slot=alert-action]]:ml-2',
  '[&_[data-slot=alert]]:[&_[data-slot=alert-action]]:-mr-0.5 [&_[data-slot=alert]]:[&_[data-slot=alert-action]]:text-[#868C98]',
  '[&_[data-slot=alert]]:[&_[data-slot=alert-action]]:[&_svg]:text-[16px]',
].join(' ');

const SystemNotificationsSnackbar = () => {
  const { toasts } = useSystemNotificationsContext();

  return (
    <div
      className={cn(
        STACK,
        ALERT,
        'dn-hidden-mobile',
        toasts.length > 2 && 'dn-snackbar-stacked',
      )}
    >
      {toasts.map((toast) => (
        <SnackbarToast key={toast.key} toast={toast} />
      ))}
    </div>
  );
};

export default SystemNotificationsSnackbar;
